import { EntityRepository } from '@record-it-npm/synchro-client';
import { EntityInfoUtils } from '@record-it-npm/synchro-common';
import React, {
  FunctionComponent,
  MutableRefObject,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useRefWithCurrentValue } from 'record-it-components/hooks/useRefWithCurrentValue';
import { StrictReactNode } from 'record-it-components/types/StrictReactNode';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { EntityChangedPayload } from 'record-it-time-common/dto/synchronization/socket/EntityChangedPayload';
import { CustomHttpHeader } from 'record-it-time-common/enums/CustomHttpHeader';
import { io, Socket } from 'socket.io-client';
import { AppEntityManager } from '../classes/entities/AppEntityManager';
import { SocketContext, SocketContextValue } from '../context/SocketContext';
import { useEntityManager } from '../hooks/entity/useEntityManager';
import { useAuth } from '../hooks/useAuth';
import { AppSynchronizationEnvironmentTypes } from '../AppSynchronizationEnvironmentTypes';
import { config } from '../config';

export const AppSocketContextProvider: FunctionComponent<{
  children: StrictReactNode;
}> = React.memo((props) => {
  const { children } = prepareProps({ props });
  const { authenticationData } = useAuth();
  const { entityManager } = useEntityManager();
  const entityManagerRef = useRefWithCurrentValue(entityManager);

  const [socket, setSocket] = useState<Socket | null>(null);
  const contextValue = useMemo<SocketContextValue>(
    () => ({
      socket
    }),
    [socket]
  );

  useEffect(() => {
    let createdSocket: Socket | null = null;

    if (authenticationData) {
      createdSocket = createSocket(
        authenticationData.csrfToken,
        entityManagerRef
      );
      setSocket(createdSocket);
    }

    return () => {
      createdSocket?.disconnect();
    };
  }, [authenticationData, entityManagerRef]);

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  );
});

function createSocket(
  csrfToken: string,
  entityManagerRef: MutableRefObject<AppEntityManager>
): Socket {
  const socket = io(config.get('SocketMainBaseUrl'), {
    path: '/websocket',
    extraHeaders: {
      [CustomHttpHeader.CsrfToken]: csrfToken
    },
    withCredentials: true
  });

  let hasConnected = false;
  socket.on('connect', () => {
    if (hasConnected) {
      // only start the actualizations on a reconnect
      void entityManagerRef.current.entityActualization.actualize();
    }

    hasConnected = true;
  });

  socket.on(
    'entityChanged',
    ({ entity, entityName }: EntityChangedPayload<any>) => {
      try {
        const repository =
          entityManagerRef.current.entityRepositoryContainer.getByEntityName(
            entityName
          ) as EntityRepository<AppSynchronizationEnvironmentTypes, any, any>;
        if (!entity.deleted) {
          repository.addExternalEntityLocally(entity);
        } else {
          EntityInfoUtils.applyDefaultValues(
            repository.getEntityInfo(),
            entity
          );
          repository.removeLocally(entity);
        }
      } catch (e) {
        // ignore repositories which couldn't be found
        // this happens when a new entity is implemented but it doesn't exist on the client yet
        if (
          e instanceof Error &&
          e.message.startsWith('no repository found for')
        ) {
          console.error(e);
        } else {
          throw e;
        }
      }
    }
  );

  return socket;
}
